// The rules of the plan lock, free of store imports so the auth slice, the API
// layer and the components can all share them.
//
// An organisation is locked when central-api enforces plans and the
// organisation has neither a running trial nor an active subscription. The
// app learns that from three places only:
//   - GET /api/v1/me          (plan_enforced, has_active_plan, plan_status)
//   - GET /api/v1/seller-init (the same three fields inside `data`)
//   - any call answered with HTTP 402 and `error: "no_active_plan"`
// Anything else (fields missing, still loading, enforcement off) is "unknown"
// and never locks.

import type { RootState } from '../../store/store';
import { isDemoMode } from '../demo/demo-mode';
import { isNewDemoMode } from '../demo-new/demo-mode';

export const NO_ACTIVE_PLAN_ERROR = 'no_active_plan';

export const SUBSCRIPTION_PAGE_ROUTE = '/settings/subscription';

export interface PlanStateFields {
  plan_enforced?: unknown;
  has_active_plan?: unknown;
  plan_status?: unknown;
}

export type PlanLockVerdict = 'locked' | 'unlocked' | 'unknown';

/**
 * What a `/me` or seller-init payload says about the lock.
 *   locked   — enforcement is on and there is no active plan
 *   unlocked — the payload positively says the organisation may use the app
 *   unknown  — the payload does not say (older API, white-label seller, ...)
 */
export function planLockVerdict(
  fields: PlanStateFields | null | undefined
): PlanLockVerdict {
  if (!fields || typeof fields !== 'object') return 'unknown';
  if (fields.plan_enforced === false) return 'unlocked';
  if (fields.plan_enforced !== true) return 'unknown';
  if (fields.has_active_plan === false) return 'locked';
  if (fields.has_active_plan === true) return 'unlocked';
  return 'unknown';
}

/** True for the refusal central-api sends on a gated route of a locked organisation. */
export function isNoActivePlanResponse(status: number, body: unknown): boolean {
  return (
    status === 402 &&
    !!body &&
    typeof body === 'object' &&
    (body as { error?: unknown }).error === NO_ACTIVE_PLAN_ERROR
  );
}

// Routes that never depend on a plan: sign-in and sign-out, public landings
// and the portals that carry their own session.
const PUBLIC_ROUTES = [
  '/register',
  '/login',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/email-verification-success',
  '/verify-email',
  '/unsubscribe',
  '/authorize',
  '/login-via-portal',
  '/client-portal',
  '/email-approve',
  '/demo',
  '/demo-new',
  '/one-pager',
];

// What a locked member who may buy a plan can still open: the subscription
// page (the Mollie return lands there with `?orderId=`), the payment landing
// pages and their own profile.
const BILLING_ROUTES = [
  SUBSCRIPTION_PAGE_ROUTE,
  '/settings/profile',
  '/stripe-subscription-success',
  '/stripe-subscription-failed',
  '/recommendations-subscription-status',
];

export const LOCKED_SETTINGS_LINKS = [
  SUBSCRIPTION_PAGE_ROUTE,
  '/settings/profile',
];

const isUnder = (pathname: string, root: string): boolean =>
  pathname === root || pathname.startsWith(`${root}/`);

/**
 * May `pathname` render while the organisation is locked? Every route that is
 * not listed here is a product route.
 */
export function isRouteOpenWhileLocked(
  pathname: string,
  canManageBilling: boolean
): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/' || path === '') return true;
  if (PUBLIC_ROUTES.some((root) => isUnder(path, root))) return true;
  return canManageBilling && BILLING_ROUTES.some((root) => isUnder(path, root));
}

/**
 * The one answer to "is this session locked?". White-label sessions and the
 * demos have no plan of their own and are never locked.
 */
export function selectIsPlanLocked(state: RootState): boolean {
  if (state.auth?.authorization?.subscriptionLock !== 'true') return false;
  if (state.UI?.isWhiteLabel) return false;
  if (isDemoMode() || isNewDemoMode()) return false;
  return true;
}

/** The kind of plan an organisation held last: a free trial or a paid plan. */
export type PlanKind = 'trial' | 'paid';

/**
 * What kind of plan the organisation held last, read from the subscription
 * rows seller-init returns for it (every row, active or not). A paid plan
 * always comes after the trial, so one paid row means the last plan was paid.
 * `null` when the organisation never had a plan, `undefined` when the payload
 * carries no subscription list at all (it then says nothing).
 */
export function lastPlanKindFromSubscriptions(
  subscriptions: unknown
): PlanKind | null | undefined {
  if (!Array.isArray(subscriptions)) return undefined;
  const planRows = subscriptions.filter(
    (row: any) =>
      !!row &&
      typeof row === 'object' &&
      row.subscription_type_slug === 'tara-ai-services'
  );
  if (planRows.length === 0) return null;
  return planRows.every((row: any) => row.is_trial === true) ? 'trial' : 'paid';
}

/**
 * Did the lock follow a trial? Only the organisation's own subscription rows,
 * fetched by seller-init for this sign-in, can say so: `plan_status` is
 * `expired` for a lapsed paid plan as well, and the persisted user slice may
 * still hold another account's subscription right after sign-in. Without
 * those rows the answer is no, and the notice says "no active plan".
 */
export function selectPlanLockFollowsTrial(state: RootState): boolean {
  return state.auth?.authorization?.lastPlanKind === 'trial';
}

/**
 * Which plan the lock follows: `trial` for a lapsed free trial, `paid` for a
 * lapsed paid plan, `null` when the organisation never had a plan or this
 * sign-in did not fetch its subscription rows.
 */
export function selectPlanLockEndedPlanKind(state: RootState): PlanKind | null {
  const kind = state.auth?.authorization?.lastPlanKind;
  return kind === 'trial' || kind === 'paid' ? kind : null;
}

/** True for the toast text of the 402 that locked the app (it must not show). */
export function selectIsPlanLockToast(
  state: RootState,
  message: unknown
): boolean {
  const notice = state.auth?.authorization?.planLockMessage;
  return (
    selectIsPlanLocked(state) &&
    typeof notice === 'string' &&
    notice !== '' &&
    message === notice
  );
}
